/**
 * D-EZ-93 / D-EZ-97 — WHAT THE 2025 PRO SEASON KNOWS ABOUT ITS DRIVERS' LIVES.
 *
 * ⚠ READ BEFORE ADDING ANYTHING. Two kinds of fact live here and they are held
 * to different standards on purpose.
 *
 * AGES ARE "REAL-ISH". Howard's ruling for D-EZ-93 asks for real drivers to get
 * real-ish ages. The years below are the drivers' publicly known birth years as
 * best recalled when this was written (4 Oct 2026) — not transcribed from a
 * document in this repository, which holds none (`docs/2025 Roster.pdf` is card
 * scans with no biographical data). A year here may be off by one; that moves a
 * retirement chance by a point or two and nothing else. Age in the 2025 season
 * is taken as 2025 minus the birth year.
 *
 * CAREER PRIORS ARE NOT GUESSED. D-EZ-97's mechanism lets a scenario start its
 * drivers with prior career wins, starts, poles, titles and earnings, and the
 * record book adds them in. The repository and the project uploads contain NO
 * career statistics for these drivers (checked: the 2025 roster PDF, the 2021
 * Xfinity card PDF and Howard's `Season Points 3.xlsx` carry ratings, not
 * records). Precise real-world numbers are not invented here, so the table is
 * EMPTY and every 2025 driver begins the league's record book at zero. To seed
 * it, fill `PRIORS_2025` from a real source and name that source in `source`.
 */

import type { CareerPrior } from '../domain/history/types.ts';

/** Approximate birth years, by 2025 roster driver id. */
export const BIRTH_YEARS_2025: Readonly<Record<string, number>> = {
  'rw25-lajoie': 1991,
  'rw25-chastain': 1992,
  'rw25-cindric': 1998,
  'rw25-adillon': 1990,
  'rw25-gragson': 1998,
  'rw25-larson': 1992,
  'rw25-keselowski': 1984,
  'rw25-haley': 1999,
  'rw25-kbusch': 1985,
  'rw25-elliott': 1995,
  'rw25-tdillon': 1992,
  'rw25-hamlin': 1980,
  'rw25-blaney': 1993,
  'rw25-allmendinger': 1981,
  'rw25-buescher': 1992,
  'rw25-briscoe': 1994,
  'rw25-bell': 1994,
  'rw25-berry': 1990,
  'rw25-logano': 1990,
  'rw25-wallace': 1993,
  'rw25-byron': 1997,
  'rw25-love': 2005,
  'rw25-hill': 1994,
  'rw25-gilliland': 2000,
  'rw25-herbst': 1999,
  'rw25-zsmith': 1999,
  'rw25-custer': 1998,
  'rw25-nemechek': 1997,
  'rw25-ejones': 1996,
  'rw25-gase': 1993,
  'rw25-kraus': 2001,
  'rw25-yeley': 1976,
  'rw25-reddick': 1996,
  'rw25-stenhouse': 1987,
  'rw25-bowman': 1993,
  'rw25-ware': 1995,
  'rw25-gibbs': 2002,
  'rw25-preece': 1990,
  'rw25-alfredo': 1999,
  'rw25-finchum': 1995,
  'rw25-mears': 1978,
  'rw25-bilicki': 1995,
  'rw25-heim': 2002,
  'rw25-mcdowell': 1984,
  'rw25-hocevar': 2003,
  'rw25-mcleod': 1983,
  'rw25-legge': 1980,
  'rw25-jjohnson': 1975,
  'rw25-zilisch': 2006,
  'rw25-svg': 1989,
  'rw25-suarez': 1992,
};

/** The age a 2025 driver races the scenario's first season at, if known. */
export function age2025(driverId: string, seasonYear = 2025): number | undefined {
  const born = BIRTH_YEARS_2025[driverId];
  return born === undefined ? undefined : seasonYear - born;
}

/**
 * D-EZ-97 — careers before the league. Intentionally empty: see the header.
 * The mechanism (`CareerPrior`, carried in `CareerCarry.priors` and added by
 * `history/registry.ts`) is live; the data is missing.
 */
export const PRIORS_2025: Readonly<Record<string, CareerPrior>> = {};
